/**
 * The run page's header (DOP-616): where you are, what state the memo is in,
 * and what you can do to it.
 *
 * One pill carries the state and the version ("Draft · v2", or "Draft ·
 * After CFO call v2" for a version a person named). The line beside it gives the
 * provenance (when, from how many files, how many sources) and what is still
 * open, as a link to the Gaps tab. "Mark reviewed" stays visible but disabled
 * while a blocking gap is open. The reason is in its title, and the memo's
 * legend says it in words too. Hiding the button would leave the reviewer
 * looking for the button the page is for.
 */

import { Download, Eye, PenLine, RefreshCw, Sparkles, Square, Users } from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";

import { type DealScreeningRun, blockingTotal, memoDealName, reviewBlockedReason } from "../../types/dealScreening";
import { countLabel, formatIsoTimestamp, memoTicker } from "./dealScreeningFormat";
import { reviewItems } from "./dealScreeningReview";
import { runHref } from "./DealsTable";
import { VersionName } from "./memoVersionLabel";
import BackButton from "../BackButton";

export interface MemoRunHeaderProps {
	run: DealScreeningRun;
	/** The workflow tab the reader came from ("review"); the back link returns there. */
	fromTab?: string | null;
	citations: number;
	onOpenGaps: () => void;
	/** Opens the Share dialog (DOP-619). */
	onShare: () => void;
	/** Opens edit mode (DOP-622). */
	onEdit: () => void;
	/** What the edit button says when the memo is shared: "Join editing". */
	editLabel?: string;
	/** Download the memo, when Share is the folder's Share (shared memos). */
	onDownload?: () => void;
	/** What this reader may do with the memo (shared memos); undefined when not shared. */
	access?: "owner" | "edit" | "view";
	/** Stops a memo that is still being written. */
	onStop?: () => void;
	stopping?: boolean;
	onReview: () => void;
	reviewing: boolean;
	onRunAgain: () => void;
	rerunning: boolean;
	analystOpen: boolean;
	onToggleAnalyst: () => void;
}

const ICON_BUTTON =
	"inline-flex h-9 w-9 items-center justify-center rounded-lg border border-gray-200 bg-white text-text-strong hover:bg-gray-50 disabled:opacity-50";

/** Stop is red text on white: it ends something, it is not the main action. */
const STOP_BUTTON =
	"inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#fecdca] bg-white px-3.5 text-sm font-medium text-[#b42318] hover:bg-[#fef3f2] disabled:opacity-50";

const SECONDARY =
	"inline-flex h-9 items-center gap-1.5 whitespace-nowrap rounded-lg border border-gray-200 bg-white px-3.5 text-sm font-medium text-text-strong hover:bg-gray-50 disabled:opacity-50";

/** "3 gaps open · 2 blocking", or null when nothing is. One word for every
 * open item, a conflict included: the count is the Gaps tab's own. The
 * blocking count is one per blocking field (DOP-658) and one per section set
 * to appear in every memo that is still to write (DOP-659). */
const openLine = (run: DealScreeningRun): string | null => {
	const blocking = blockingTotal(run);
	const open = Math.max(reviewItems(run).length, blocking);
	if (open === 0) return null;
	return `${countLabel(open, "gap")} open${blocking > 0 ? ` · ${blocking} blocking` : ""}`;
};

const MemoRunHeader: React.FC<MemoRunHeaderProps> = ({
	run,
	fromTab,
	citations,
	onOpenGaps,
	onShare,
	onEdit,
	editLabel,
	onDownload,
	access,
	onStop,
	stopping = false,
	onReview,
	reviewing,
	onRunAgain,
	rerunning,
	analystOpen,
	onToggleAnalyst,
}) => {
	const reviewed = run.status === "reviewed";
	// Only the newest version can be marked reviewed (Turtleback 10-03): the
	// deal list reads the newest one, so a mark on an earlier version never
	// showed there. Restoring this version makes it the newest.
	const older = Boolean(run.latest_run_id && run.latest_run_id !== run.public_id);
	const blockedReason = older
		? `Version ${run.latest_version} is the newest. Open it, or restore this version, to mark it reviewed`
		: reviewBlockedReason(run);
	const workflowHref = `/workflows/deal-screening/${encodeURIComponent(run.workflow.public_id)}`;
	const ticker = memoTicker(run.facts, memoDealName(run));
	const back =
		fromTab === "review"
			? { href: `${workflowHref}?tab=review`, label: "Back to Gaps" }
			: { href: workflowHref, label: "Back to all deals" };
	const open = openLine(run);

	const stopped = run.generation_error?.startsWith("Stopped by") ?? false;

	return (
		<header className="flex items-start gap-3 bg-white px-7 pb-3 pt-4">
			{/* The back button is the way out a reader looks for (DOP-511). */}
			<BackButton to={back.href} label={back.label} className="mt-0.5" />
			<div className="min-w-0 flex-1">
				<div className="flex min-w-0 items-center gap-2.5">
					<h1 className="truncate text-xl font-semibold text-[#111111]">
						{memoDealName(run)}
						{/* A listed company's name carries where it trades (DOP-661). */}
						{ticker && (
							<span className="font-normal text-[#6B7280]" data-testid="run-ticker">
								{" "}
								({ticker})
							</span>
						)}
					</h1>
					<span
						className={`inline-flex h-6 min-w-0 max-w-[50%] shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 text-xs font-semibold ${
							reviewed ? "bg-[#ecfdf3] text-[#067647]" : "bg-[#f2f2f2] text-[#3f4247]"
						}`}
						data-testid="run-pills"
					>
						{reviewed ? "Reviewed" : "Draft"} ·{" "}
						<VersionName version={run.version} name={run.name} nameTestId="run-version-name" />
					</span>
				</div>
				{/* One quiet line: which version, how it was made, what is open. */}
				<p className="mt-0.5 truncate text-xs text-[#6B7280]">
					{run.latest_run_id && run.latest_run_id !== run.public_id && (
						// An earlier version on screen says so (DOP-653): a change
						// applied since is in the newest one, not here.
						<span className="font-medium text-[#b54708]" data-testid="run-newer-version">
							Version {run.latest_version} is the newest ·{" "}
							<Link to={runHref(run.latest_run_id, "memo")} className="underline underline-offset-2">
								Open it
							</Link>
							{" · "}
						</span>
					)}
					{/* A named version says its name in the pill; an unnamed save says whose it is. */}
					{!run.name && run.origin === "edited" && (
						<span className="font-medium text-[#5B3FA8]" data-testid="run-version-name">
							Your edits
							{" · "}
						</span>
					)}
					{run.generation_error ? (
						<span role="alert" className={stopped ? "text-[#6B7280]" : "text-red-600"}>
							{run.generation_error}
						</span>
					) : run.generating ? (
						<>Running…</>
					) : (
						<>
							Written {formatIsoTimestamp(run.generated_at)} from{" "}
							{countLabel(run.report.files.length, "file")} · {countLabel(citations, "source")}
							{open && (
								<>
									{" · "}
									<button
										type="button"
										onClick={onOpenGaps}
										className="font-medium text-[#b54708] underline-offset-2 hover:underline"
									>
										{open}
									</button>
								</>
							)}
						</>
					)}
				</p>
			</div>

			<div className="flex shrink-0 items-center gap-1.5">
				{run.generating && onStop ? (
					<button
						type="button"
						onClick={onStop}
						disabled={stopping}
						className={`${STOP_BUTTON}`}
					>
						<Square className="h-3 w-3 fill-current" aria-hidden />
						{stopping ? "Stopping…" : "Stop"}
					</button>
				) : (
					<>
						{access && access !== "owner" && (
							<span
								className="inline-flex h-6 items-center gap-1 rounded-full bg-[#f2f2f2] px-2 text-xs font-medium text-[#3f4247]"
								data-testid="run-access"
								title={access === "view" ? "Shared with you view-only" : "Shared with you; you can edit"}
							>
								{access === "view" ? <Eye className="h-3 w-3" aria-hidden /> : <Users className="h-3 w-3" aria-hidden />}
								{access === "view" ? "View only" : "Shared"}
							</span>
						)}
						{editLabel ? (
							<button
								type="button"
								onClick={onEdit}
								disabled={!run.generated_at}
								className={SECONDARY}
								title={editLabel}
							>
								<PenLine className="h-4 w-4" aria-hidden />
								{editLabel}
							</button>
						) : (
							<button
								type="button"
								onClick={onEdit}
								disabled={!run.generated_at}
								className={ICON_BUTTON}
								aria-label="Edit memo"
								title="Edit memo"
							>
								<PenLine className="h-4 w-4" aria-hidden />
							</button>
						)}
						{onDownload && (
							<button
								type="button"
								onClick={onDownload}
								disabled={!run.generated_at}
								className={SECONDARY}
								title="Download the memo"
							>
								<Download className="h-4 w-4" aria-hidden />
								Download
							</button>
						)}
						{(access === undefined || access === "owner") && (
							<button
								type="button"
								onClick={onShare}
								disabled={!run.generated_at}
								className={SECONDARY}
								title={access === "owner" ? "Share with the team" : "Share"}
							>
								<Users className="h-4 w-4" aria-hidden />
								Share
							</button>
						)}
						<button
							type="button"
							onClick={onToggleAnalyst}
							aria-pressed={analystOpen}
							className={`${SECONDARY} ${analystOpen ? "border-[#99CFCD] bg-[#E6F3F3] text-[#004C4B] hover:bg-[#E6F3F3]" : ""}`}
						>
							<Sparkles className="h-4 w-4" aria-hidden />
							Chat
						</button>
						{reviewed ? (
							<button type="button" onClick={onRunAgain} disabled={rerunning} className={SECONDARY}>
								<RefreshCw className="h-4 w-4" aria-hidden />
								Re-run
							</button>
						) : (
							<button
								type="button"
								onClick={onReview}
								disabled={blockedReason !== null || reviewing}
								title={blockedReason ?? undefined}
								className="inline-flex h-9 items-center rounded-lg bg-[#004C4B] px-3.5 text-sm font-semibold text-white hover:bg-[#003a3a] disabled:opacity-50"
							>
								{reviewing ? "Saving…" : "Mark reviewed"}
							</button>
						)}
					</>
				)}
			</div>
		</header>
	);
};

export default MemoRunHeader;
